/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { CSSProperties } from 'react';
import { DashboardRow, DashboardSection } from '@g17eco/molecules/dashboard';
import { Progress } from 'reactstrap';
import { StatusStats } from '@g17eco/types/initiativeStats';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { roundTo } from '@utils/number';
import { truncate } from '@utils/string';
import { QUESTION } from '@constants/terminology';

interface ProgressBarProps {
  progress: StatusStats;
  style?: CSSProperties;
  className?: string;
  displayValues?: boolean;
}

interface ProgressRowProps {
  title?: string | JSX.Element;
  progress: StatusStats;
  style?: CSSProperties;
  displayValues?: boolean;
}

const MISSING_DELEGATION_MESSAGE = `No ${QUESTION.PLURAL} delegated for this user based on set filters`

// Reusable progress bar component (just the bar, no wrapper)
export const SurveyProgressBar = ({
  progress,
  style,
  className = 'mx-2 w-100',
  displayValues = true,
}: ProgressBarProps) => {
  const total = progress.created + progress.updated + progress.rejected + progress.verified;
  return displayValues ? (
    <Progress className={className} style={style} multi>
      <Progress className={'background-ThemeAccentMedium'} bar value={progress.updated} max={total}>
        {progress.updated}%
      </Progress>
      <Progress className={'background-ThemeSuccessMedium'} bar value={progress.verified} max={total}>
        {progress.verified}%
      </Progress>
      <Progress className={'background-ThemeDangerMedium'} bar value={progress.rejected} max={total}>
        {progress.rejected}%
      </Progress>
      <Progress className={'background-ThemeTextLight'} bar value={progress.created} max={total}>
        {progress.created}%
      </Progress>
    </Progress>
  ) : (
    <Progress className={className} style={style} multi>
      <Progress className={'background-ThemeAccentMedium'} bar value={progress.updated} max={total} />
      <Progress className={'background-ThemeSuccessMedium'} bar value={progress.verified} max={total} />
      <Progress className={'background-ThemeDangerMedium'} bar value={progress.rejected} max={total} />
      <Progress className={'background-ThemeTextLight'} bar value={progress.created} max={total} />
    </Progress>
  );
};

// Full progress row with title and legend
export const ProgressRow = ({ title, progress, style, displayValues = true }: ProgressRowProps) => {
  return (
    <DashboardRow
      className={'mb-5'}
      headingStyle={6}
      title={title}
      buttons={[
        <span className={'mr-3'} key={'created'}>
          <i className={'fa-solid fa-square mr-1 text-ThemeTextLight'} />
          Unanswered
        </span>,
        <span className={'mr-3'} key={'updated'}>
          <i className={'fa-solid fa-square mr-1 text-ThemeAccentMedium'} />
          Answered
        </span>,
        <span className={'mr-3'} key={'rejected'}>
          <i className={'fa-solid fa-square mr-1 text-ThemeDangerMedium'} />
          Rejected
        </span>,
        <span className={'mr-3'} key={'verified'}>
          <i className={'fa-solid fa-square mr-1 text-ThemeSuccessMedium'} />
          Verified
        </span>,
      ]}
    >
      <SurveyProgressBar progress={progress} style={{ ...style, marginTop: '0.5rem' }} displayValues={displayValues} />
    </DashboardRow>
  );
};

export const OverviewProgressRow = ({ title, progress, style, displayValues = true }: ProgressRowProps) => {
  const tooltipContent = (
    <div className='text-start'>
      <div>Unanswered: {progress.created}%</div>
      <div>Answered: {progress.updated}%</div>
      <div>Verified: {progress.verified}%</div>
      <div>Rejected: {progress.rejected}%</div>
    </div>
  );

  return (
    <DashboardSection padding={0}>
      {title}
      <SimpleTooltip text={tooltipContent}>
        <SurveyProgressBar
          className=''
          progress={progress}
          style={{ ...style, marginTop: '0.5rem' }}
          displayValues={displayValues}
        />
      </SimpleTooltip>
      <div className='mt-2 d-flex justify-content-end'>
        <span className={'mr-3'} key={'created'}>
          <i className={'fa-solid fa-square mr-1 text-ThemeTextLight'} />
          Unanswered
        </span>
        <span className={'mr-3'} key={'updated'}>
          <i className={'fa-solid fa-square mr-1 text-ThemeAccentMedium'} />
          Answered
        </span>
        <span className={'mr-3'} key={'rejected'}>
          <i className={'fa-solid fa-square mr-1 text-ThemeDangerMedium'} />
          Rejected
        </span>
        <span key={'verified'}>
          <i className={'fa-solid fa-square mr-1 text-ThemeSuccessMedium'} />
          Verified
        </span>
      </div>
    </DashboardSection>
  );
};

interface UserProgressRowProps {
  numerator: number;
  denominator: number;
  className?: string;
  tooltip?: string;
  maxDigits?: number;
}

// Helper function to truncate fraction with ellipsis
const truncateFraction = ({
  numerator,
  denominator,
  maxDigits,
}: {
  numerator: number;
  denominator: number;
  maxDigits: number;
}) => {
  const [num, denom] = [numerator, denominator].map((part) => {
    const partStr = String(part);
    return truncate(partStr, maxDigits);
  });

  return {
    fraction: `${num}/${denom}`,
    isTruncated: num !== String(numerator) || denom !== String(denominator),
  };
};

export const UserProgressRow = ({
  numerator,
  denominator,
  className = 'background-ThemeAccentMedium',
  maxDigits = 4,
}: UserProgressRowProps) => {
  if (!denominator) {
    return (
      <SimpleTooltip text={MISSING_DELEGATION_MESSAGE}>
        <Progress
          style={{
            height: '0.25rem',
          }}
          multi
        >
          <Progress className={'background-ThemeNeutralsDark'} bar value={100} />
        </Progress>
      </SimpleTooltip>
    );
  }

  const { fraction, isTruncated } = truncateFraction({ numerator, denominator, maxDigits });

  return (
    <div className='d-flex justify-content-between'>
      <span className='col-3'>
        <SimpleTooltip text={isTruncated ? `${numerator}/${denominator}` : ''}>
          <span>{fraction}</span>
        </SimpleTooltip>
      </span>
      <Progress multi className='col-8'>
        <Progress className={className} bar value={roundTo((numerator / denominator) * 100)} />
      </Progress>
    </div>
  );
};
